import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, invitesAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || '';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore(state => state.setAuth);

  // From an /invite link: sign in or sign up to accept it.
  const inviteToken = searchParams.get('invite') || '';

  const [mode, setMode] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pick up error passed from OAuth callback
  useEffect(() => {
    const err = searchParams.get('error');
    if (err) setError(decodeURIComponent(err));
  }, []);

  // Say what the invite is for, and fill in the address it was sent to — it can
  // only be accepted by an account with that address.
  useEffect(() => {
    if (!inviteToken) return;
    invitesAPI.lookup(inviteToken)
      .then(r => {
        setInvite(r.data);
        setEmail(current => current || r.data.email);
      })
      .catch(() => {});
  }, [inviteToken]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let response;
      if (mode === 'login') {
        response = await authAPI.login(email, password);
      } else {
        response = await authAPI.register(
          email,
          password,
          inviteToken ? undefined : (accountName || undefined),
          inviteToken || undefined
        );
      }

      const { token, user } = response.data;
      setAuth(token, user);
      // An OAuth consent flow that bounced us here is resumed first — otherwise
      // the client's authorization parameters are lost and it has to start over.
      const oauthReturn = sessionStorage.getItem('mc_oauth_return');
      if (oauthReturn) {
        sessionStorage.removeItem('mc_oauth_return');
        navigate(oauthReturn);
      } else if (inviteToken && mode === 'login') {
        // Signed in to an existing account: accept on the invite page.
        navigate(`/invite/${encodeURIComponent(inviteToken)}`);
      } else if (mode === 'register' && response.data.created_account) {
        // A brand-new account of their own starts on the billing welcome.
        navigate('/billing?welcome=1');
      } else {
        navigate('/');
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'An error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  function handleGoogleSignIn() {
    const params = new URLSearchParams();
    if (inviteToken) {
      params.set('invite', inviteToken);
      // A Google sign-in to an existing account comes back without accepting,
      // so the callback returns here to finish.
      sessionStorage.setItem('mc_invite_return', inviteToken);
    }
    const url = `${BACKEND_URL}/api/auth/google${params.toString() ? '?' + params : ''}`;
    window.location.href = url;
  }

  const inviteTarget = invite
    ? invite.kind === 'member'
      ? `join ${invite.organisation_name}`
      : `work in ${invite.workspace_name} (${invite.organisation_name})`
    : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-red-600 rounded-lg mb-4">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="9" strokeWidth="2" />
              <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">Mission Control</h1>
          <p className="text-gray-500 text-sm mt-1">Live sports production system</p>
        </div>

        {inviteTarget && (
          <div className="mb-4 px-4 py-3 bg-blue-950/40 border border-blue-800 rounded-lg text-sm text-blue-200 leading-relaxed">
            You&rsquo;ve been invited to <span className="text-white font-medium">{inviteTarget}</span>.
            Sign in or create an account with <span className="text-white">{invite.email}</span> to accept.
          </div>
        )}

        {/* Form Card */}
        <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg p-6">
          {/* Mode Toggle */}
          <div className="flex mb-6 bg-[#0a0a0a] rounded-md p-1">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-1.5 text-sm font-medium rounded transition-colors ${
                mode === 'login'
                  ? 'bg-[#1a1a1a] text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-1.5 text-sm font-medium rounded transition-colors ${
                mode === 'register'
                  ? 'bg-[#1a1a1a] text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Register
            </button>
          </div>

          {error && (
            <div className="mb-4 px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign-In */}
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-100 text-gray-800 text-sm font-medium rounded transition-colors mb-4"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[#2a2a2a]" />
            <span className="text-xs text-gray-600">or</span>
            <div className="flex-1 h-px bg-[#2a2a2a]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Signing up from an invite joins what you were invited to; otherwise
                it opens an account of your own, named here. */}
            {mode === 'register' && !inviteToken && (
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                  Account Name
                </label>
                <input
                  type="text"
                  value={accountName}
                  onChange={e => setAccountName(e.target.value)}
                  placeholder="My Production Company"
                  className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                />
                <p className="mt-1 text-xs text-gray-600">
                  Starts a 14-day free trial. Joining someone else&rsquo;s account? Use the invite link they sent you.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoComplete="email"
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                minLength={mode === 'register' ? 8 : undefined}
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
              />
              {mode === 'register' && (
                <p className="mt-1 text-xs text-gray-600">Minimum 8 characters</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded text-sm transition-colors"
            >
              {loading
                ? (mode === 'login' ? 'Signing in...' : 'Creating account...')
                : (mode === 'login' ? 'Sign In' : 'Create Account')
              }
            </button>
          </form>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">
          Mission Control v1.0 — Production Use Only
        </p>
      </div>
    </div>
  );
}
